<template>
  <q-dialog
    ref="dialogRef"
    :maximized="$q.screen.xs"
    :full-height="fullHeight"
    :style="
      props.transformOrigin
        ? `transform-origin: ${props.transformOrigin[0]}px ${props.transformOrigin[1]}px;`
        : null
    "
    @hide="onDialogHide"
    @ok="onDialogOK"
  >
    <q-card
      ref="cardRef"
      class="column no-wrap text-black card"
      :class="{
        'doodle-container doodle-right': $q.screen.gt.xs,
        'full-height': props.fullHeight,
        'bg-black': props.dark,
        'bg-primary': !props.dark,
        'text-black': !props.dark,
        'text-white': props.dark,
      }"
      :style="{ overflow: 'hidden', ...(props.cardStyle || {}) }"
    >
      <Suspense v-if="!error">
        <template #default>
          <slot></slot>
        </template>

        <template #fallback>
          <div class="row justify-center items-center" style="min-height: 70vh">
            <q-spinner size="xl" color="black" />
          </div>
        </template>
      </Suspense>

      <div v-else class="column justify-center items-center q-py-xl text-black">
        <q-icon name="warning" size="40px" class="q-mb-lg" />
        <p class="text-h5 text-weight-bold">An error occurred</p>
        <p class="text-center" style="max-width: 400px">{{ error.message }}</p>
      </div>
    </q-card>
  </q-dialog>
</template>

<script lang="ts" setup>
import { ref, onErrorCaptured, provide } from "vue";
import type { QDialogProps } from "quasar";
import { useQuasar, useDialogPluginComponent } from "quasar";
import { dialogInjectionKey } from ".";
import * as Sentry from "@sentry/vue";
import { HandledError } from "@/utils/errors";

const props = withDefaults(
  defineProps<
    QDialogProps & {
      transformOrigin?: [number, number];
      cardStyle?: Record<string, string>;
      dark?: boolean;
    }
  >(),
  {
    dark: false,
  },
);

defineEmits({ ...useDialogPluginComponent.emitsObject });

const $q = useQuasar();
const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } =
  useDialogPluginComponent();
defineExpose({ onDialogHide, onDialogOK, onDialogCancel });
provide(dialogInjectionKey, { onDialogHide, onDialogOK, onDialogCancel });

const error = ref<Error>();
onErrorCaptured((capturedError: Error) => {
  if (capturedError instanceof HandledError) {
    return true;
  }
  error.value = capturedError;
  console.error(capturedError);
  Sentry.captureException(capturedError);
  return true;
});
</script>

<style lang="scss" scoped>
.q-dialog :deep(.q-dialog__inner) {
  transform-origin: inherit;
}

.card {
  width: 100%;
  max-width: 900px;
  height: auto;
  max-height: 100%;
}
</style>
